import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ContractsService } from '../../../services/contracts.service';
import { ProductsService } from '../../../services/products.service';
import { VehiclesService } from '../../../services/vehicles.service';
import { PaymentService } from '../../../services/payment.service';
import { AccountsReceivableService } from '../../../services/accounts-receivable.service';
import { firstValueFrom, Observable, of, Subject, forkJoin } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { Product } from '../../../models/Product';
import { Vehicle } from '../../../models/Vehicle';
import { AsaasCharge, AsaasChargeBatch, AsaasCustomer } from '../../../models/Payment';
import { AccountsReceivableCreate } from '../../../models/AccountsReceivable';
import { ErrorService } from '../../../shared/services/error.service';

@Component({
  selector: 'app-contract-renew',
  templateUrl: './contract-renew.component.html',
  styleUrls: ['./contract-renew.component.css']
})
export class ContractRenewComponent implements OnInit, OnDestroy {
  contractForm: FormGroup;
  contractId!: number;
  isLoading = false;
  isSaving = false;
  contractData: any = null;
  contract: any = null;
  driver: any = null;
  vehicleSnapshot: any = null;
  product: any = null;
  products: Product[] = [];

  currentStep = 1;
  readonly totalSteps = 2;

  // Caução: valor antigo já pago vs novo plano
  previousDepositAmount = 0;
  previousDepositPaid = false;
  previousProductId: number | null = null;
  newDepositAmount = 0;
  depositDifference = 0;

  // 1ª parcela: manter o que está pendente e cobrar apenas diferença
  previousFirstInstallmentAmount = 0;
  previousFirstInstallmentPaid = false;
  firstInstallmentDifference = 0;

  // Valor da 1ª parcela do novo plano (referência, não o que será cobrado)
  newBaseAmount = 0;
  contractWeeksCount = 4;

  // Métodos de pagamento
  paymentMethods = [
    { value: 'CASH', label: 'Dinheiro' },
    { value: 'PIX', label: 'Pix' },
    { value: 'BOLETO', label: 'Boleto' },
    { value: 'CREDIT_CARD', label: 'Cartão (Asaas)' }
  ];

  depositPayments = [
    { method: 'CASH', amount: 0 },
    { method: 'PIX', amount: 0 }
  ];

  firstInstallmentPayments = [
    { method: 'CASH', amount: 0 },
    { method: 'PIX', amount: 0 }
  ];

  depositPaymentMode: 'single' | 'split' = 'single';
  firstInstallmentPaymentMode: 'single' | 'split' = 'single';
  depositInstallments = 1;

  // Asaas
  asaasCustomerId: string | null = null;
  asaasCustomerLoading = false;
  asaasCustomerError: string | null = null;
  asaasCustomerInfo: AsaasCustomer | null = null;

  // Cartão 1
  depositCardInstallments = 1;
  creditCardHolderName = '';
  creditCardNumber = '';
  creditCardExpiryMonth = '';
  creditCardExpiryYear = '';
  creditCardCcv = '';

  // Cartão 2
  depositCardInstallments2 = 1;
  creditCardHolderName2 = '';
  creditCardNumber2 = '';
  creditCardExpiryMonth2 = '';
  creditCardExpiryYear2 = '';
  creditCardCcv2 = '';

  isCardBack = false;
  isCardBack2 = false;

  // Busca de veículo por placa
  vehicleSearchResults: Vehicle[] = [];
  isSearchingVehicles = false;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private contractsService: ContractsService,
    private productsService: ProductsService,
    private vehiclesService: VehiclesService,
    private paymentService: PaymentService,
    private accountsReceivableService: AccountsReceivableService,
    private router: Router,
    private route: ActivatedRoute,
    private errorService: ErrorService
  ) {
    this.contractForm = this.fb.group({
      customerId: [null],
      vehicleId: [null],
      productId: ['', Validators.required],
      documentTemplateVersionId: [null],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      billingCycle: ['', Validators.required],
      status: ['Pendente'],
      placeholdersDataJson: ['{}'],
      signatureProvider: ['ClickSign'],
      driverName: ['', Validators.required],
      driverCpfCnpj: ['', Validators.required],
      driverCnhNumber: [''],
      driverCnhExpiryDate: [''],
      driverEmail: [''],
      driverPhone: [''],
      driverStreet: ['', Validators.required],
      driverNumber: ['', Validators.required],
      driverComplement: [''],
      driverDistrict: ['', Validators.required],
      driverCity: ['', Validators.required],
      driverState: ['', Validators.required],
      driverZipCode: ['', Validators.required],
      vehiclePlate: ['', Validators.required],
      vehicleModel: ['', Validators.required],
      vehicleYear: [null, Validators.required],
      vehicleColor: [''],
      vehicleBrand: [''],
      vehicleRenavam: [''],
      vehicleChassis: [''],
      vehicleCurrentKm: [null],
      vehicleInspectionNotes: [''],
      /** Renovação (mesmo fluxo) vs migração (registro explícito nas observações). */
      renewalOperationType: ['renewal', Validators.required]
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadInitialData();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupListeners(): void {
    this.contractForm.get('productId')?.valueChanges.subscribe((productId) => {
      if (productId) {
        const productIdNum = Number(productId);
        if (productIdNum && this.products.length > 0) {
          this.recalculateEndDate(productIdNum);
          this.recalculateDeposit(productIdNum);
        }
      }
    });

    this.contractForm.get('vehiclePlate')?.valueChanges
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe((plate: string) => {
        const term = (plate || '').trim();
        if (term.length >= 3) {
          this.searchVehicleByPlate(term);
        } else {
          this.vehicleSearchResults = [];
        }
      });
  }

  searchVehicleByPlate(plate?: string): void {
    const term = (plate ?? this.contractForm.get('vehiclePlate')?.value ?? '').trim();
    if (!term) {
      this.errorService.showInfoMessage('Digite a placa do veículo para buscar.');
      return;
    }
    this.isSearchingVehicles = true;
    this.vehicleSearchResults = [];
    this.vehiclesService.getAllPaginated(1, 50, { search: term })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.vehicleSearchResults = result.items || [];
          this.isSearchingVehicles = false;
          if (this.vehicleSearchResults.length === 0) {
            this.errorService.showInfoMessage('Nenhum veículo encontrado para: ' + term);
          }
        },
        error: (err) => {
          this.isSearchingVehicles = false;
          this.errorService.showErrorMessage('Erro ao buscar veículo: ' + (err?.message || 'Erro desconhecido'));
        }
      });
  }

  onSelectVehicle(vehicle: Vehicle): void {
    this.contractForm.patchValue({
      vehicleId: vehicle.id,
      vehiclePlate: vehicle.plateNumber || '',
      vehicleModel: vehicle.model || '',
      vehicleYear: vehicle.modelYear || new Date().getFullYear(),
      vehicleColor: vehicle.color || '',
      vehicleBrand: vehicle.brand || '',
      vehicleRenavam: vehicle.renavam || vehicle.renavamNumber || '',
      vehicleChassis: vehicle.chassisNumber || '',
      vehicleCurrentKm: vehicle.currentMileage ?? null
    }, { emitEvent: false });
    this.vehicleSearchResults = [];
    this.errorService.showInfoMessage(`Veículo ${vehicle.plateNumber} selecionado. Dados preenchidos.`);
  }

  onProductChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const productId = target.value;
    if (productId) {
      const productIdNum = Number(productId);
      if (productIdNum && this.products.length > 0) {
        this.recalculateEndDate(productIdNum);
        this.recalculateDeposit(productIdNum);
      }
    }
  }

  async loadInitialData(): Promise<void> {
    this.isLoading = true;
    try {
      const products = await firstValueFrom(this.productsService.getProducts());
      this.products = products.filter(p => p.isActive);
      await this.loadContractComplete();
      this.setupListeners();
      this.isLoading = false;
    } catch (error: any) {
      this.errorService.showErrorMessage('Erro ao carregar dados: ' + error.message);
      this.isLoading = false;
    }
  }

  async loadContractComplete(): Promise<void> {
    try {
      const contractData = await firstValueFrom(
        this.contractsService.getContractComplete(this.contractId)
      );
      this.contractData = contractData;
      this.contract = contractData.contract;
      this.driver = contractData.driver;
      this.vehicleSnapshot = contractData.vehicleSnapshot;
      this.product = contractData.product;

      this.previousProductId = Number(this.contract?.productId) || null;
      this.previousDepositAmount = Number(this.contract?.depositAmount) || 0;
      this.previousDepositPaid = !!this.contract?.depositPaid;
      this.previousFirstInstallmentPaid = !!this.contract?.firstInstallmentPaid;
      this.previousFirstInstallmentAmount = Number(this.contract?.baseAmount) || 0;
      if (!this.previousDepositAmount && this.product) {
        this.previousDepositAmount = Number(this.product.depositAmount) || 0;
      }

      this.populateForm();
      this.lookupAsaasCustomer();
    } catch (error: any) {
      this.errorService.showErrorMessage('Erro ao carregar contrato: ' + error.message);
      this.router.navigate(['/contracts']);
    }
  }

  private lookupAsaasCustomer(): void {
    const cpfCnpj = (this.driver?.cpfCnpj || '').replace(/\D/g, '');
    if (!cpfCnpj || (cpfCnpj.length !== 11 && cpfCnpj.length !== 14)) {
      return;
    }
    this.asaasCustomerLoading = true;
    this.asaasCustomerError = null;
    this.paymentService.listAsaasCustomers({ cpfCnpj })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.asaasCustomerLoading = false;
          const first = res?.data?.[0];
          this.asaasCustomerId = first?.id ?? null;
          this.asaasCustomerInfo = first || null;
          if (!this.asaasCustomerId && res?.data && (res.data as any[]).length === 0) {
            this.asaasCustomerError = 'Cliente não encontrado no Asaas para este CPF/CNPJ.';
          }
        },
        error: (err) => {
          this.asaasCustomerLoading = false;
          this.asaasCustomerId = null;
          this.asaasCustomerError = err?.message || 'Erro ao consultar cliente Asaas.';
          this.asaasCustomerInfo = null;
        }
      });
  }

  populateForm(): void {
    const today = this.getTodayDateString();
    this.contractForm.patchValue({
      customerId: this.contract.customerId || null,
      vehicleId: this.contract.vehicleId || null,
      productId: this.contract.productId || '',
      documentTemplateVersionId: this.contract.documentTemplateVersionId || null,
      startDate: today,
      endDate: '',
      billingCycle: this.contract.billingCycle || 'Semanal',
      status: 'Pend. Assin.',
      placeholdersDataJson: this.contract.placeholdersDataJson || '{}',
      signatureProvider: 'ClickSign'
    });
    this.contractForm.get('startDate')?.disable({ emitEvent: false });
    if (this.contract.productId && this.products.length > 0) {
      setTimeout(() => {
        this.recalculateEndDate(this.contract.productId);
        this.recalculateDeposit(this.contract.productId);
      }, 100);
    }
    if (this.driver) {
      const address = this.driver.address || this.driver;
      this.contractForm.patchValue({
        driverName: this.driver.name || '',
        driverCpfCnpj: this.driver.cpfCnpj || '',
        driverCnhNumber: this.driver.cnhNumber || '',
        driverCnhExpiryDate: this.formatDateForInput(this.driver.cnhExpiryDate) || '',
        driverEmail: this.driver.email || '',
        driverPhone: this.driver.phone || '',
        driverStreet: address.street || '',
        driverNumber: address.number || '',
        driverComplement: address.complement || '',
        driverDistrict: address.district || '',
        driverCity: address.city || '',
        driverState: address.state || '',
        driverZipCode: address.zipCode || ''
      });
    }
    if (this.vehicleSnapshot) {
      this.contractForm.patchValue({
        vehiclePlate: this.vehicleSnapshot.plate || '',
        vehicleModel: this.vehicleSnapshot.model || '',
        vehicleYear: this.vehicleSnapshot.year || null,
        vehicleColor: this.vehicleSnapshot.color || '',
        vehicleBrand: this.vehicleSnapshot.brand || '',
        vehicleRenavam: this.vehicleSnapshot.renavam || '',
        vehicleChassis: this.vehicleSnapshot.chassis || '',
        vehicleCurrentKm: this.vehicleSnapshot.currentKm || 0,
        vehicleInspectionNotes: this.vehicleSnapshot.inspectionNotes || ''
      });
    }
  }

  recalculateEndDate(productId: number | string): void {
    const productIdNum = typeof productId === 'string' ? Number(productId) : productId;
    const product = this.products.find(p => p.id === productIdNum);
    if (!product) return;
    const startDate = this.contractForm.get('startDate')?.value || this.getTodayDateString();
    const days = Number(product.days) || 30;
    const endDate = this.addDaysToDateString(startDate, days);
    this.contractForm.patchValue({ endDate }, { emitEvent: false });
  }

  recalculateDeposit(productId: number | string): void {
    const productIdNum = typeof productId === 'string' ? Number(productId) : productId;
    const product = this.products.find(p => p.id === productIdNum);
    if (!product) return;

    this.newDepositAmount = Number(product.depositAmount) || 0;
    this.newBaseAmount = Number(product.basePrice) || 0;

    const days = Number(product.days) || 0;
    let parcelas = Math.floor(days / 7);
    if (parcelas < 1) parcelas = 1;
    this.contractWeeksCount = parcelas;

    this.depositDifference = Math.max(0, this.newDepositAmount - this.previousDepositAmount);
    // Se o 1º pagamento do plano anterior (1ª parcela) ainda não foi pago/compensado,
    // mantemos ele e cobramos apenas a diferença para o novo plano.
    this.firstInstallmentDifference = Math.max(
      0,
      this.newBaseAmount - this.previousFirstInstallmentAmount
    );

    this.initPaymentSplits(this.firstInstallmentDifference, this.depositDifference);
  }

  // ======== Gestão de pagamentos (split) ========

  private initPaymentSplits(firstInstallmentTotal: number, depositTotal: number): void {
    this.depositPayments = [
      { method: this.depositPayments[0]?.method || 'CASH', amount: depositTotal },
      { method: this.depositPayments[1]?.method || 'PIX', amount: 0 }
    ];
    this.firstInstallmentPayments = [
      { method: this.firstInstallmentPayments[0]?.method || 'CASH', amount: firstInstallmentTotal },
      { method: this.firstInstallmentPayments[1]?.method || 'PIX', amount: 0 }
    ];
    this.depositPaymentMode = 'single';
    this.firstInstallmentPaymentMode = 'single';
  }

  private getDepositTotal(): number {
    return this.depositDifference;
  }

  private getFirstInstallmentTotal(): number {
    return this.firstInstallmentDifference;
  }

  setDepositPaymentMode(mode: 'single' | 'split'): void {
    this.depositPaymentMode = mode;
    this.recalculateDepositSplit();
  }

  setFirstInstallmentPaymentMode(mode: 'single' | 'split'): void {
    this.firstInstallmentPaymentMode = mode;
    this.recalculateFirstInstallmentSplit();
  }

  private recalculateDepositSplit(): void {
    const total = this.getDepositTotal();
    if (total <= 0) {
      this.depositPayments[0].amount = 0;
      this.depositPayments[1].amount = 0;
      return;
    }
    if (this.depositPaymentMode === 'single') {
      this.depositPayments[0].amount = total;
      this.depositPayments[1].amount = 0;
    } else {
      let first = Number(this.depositPayments[0].amount) || 0;
      if (first <= 0 || first >= total) {
        first = Math.round((total / 2) * 100) / 100;
      }
      this.depositPayments[0].amount = first;
      this.depositPayments[1].amount = total - first;
    }
  }

  private recalculateFirstInstallmentSplit(): void {
    const total = this.getFirstInstallmentTotal();
    if (total <= 0) {
      this.firstInstallmentPayments[0].amount = 0;
      this.firstInstallmentPayments[1].amount = 0;
      return;
    }
    if (this.firstInstallmentPaymentMode === 'single') {
      this.firstInstallmentPayments[0].amount = total;
      this.firstInstallmentPayments[1].amount = 0;
    } else {
      let first = Number(this.firstInstallmentPayments[0].amount) || 0;
      if (first <= 0 || first >= total) {
        first = Math.round((total / 2) * 100) / 100;
      }
      this.firstInstallmentPayments[0].amount = first;
      this.firstInstallmentPayments[1].amount = total - first;
    }
  }

  onDepositAmountChange(index: number, value: number): void {
    const total = this.getDepositTotal();
    if (total <= 0) {
      this.depositPayments[0].amount = 0;
      this.depositPayments[1].amount = 0;
      return;
    }
    const v = Math.max(0, Number(value) || 0);
    if (this.depositPaymentMode === 'single') {
      this.depositPayments[0].amount = Math.min(v, total);
      this.depositPayments[1].amount = 0;
      return;
    }
    if (index === 0) {
      const first = Math.min(v, total);
      this.depositPayments[0].amount = first;
      this.depositPayments[1].amount = total - first;
    } else {
      const second = Math.min(v, total);
      this.depositPayments[1].amount = second;
      this.depositPayments[0].amount = total - second;
    }
  }

  onFirstInstallmentAmountChange(index: number, value: number): void {
    const total = this.getFirstInstallmentTotal();
    if (total <= 0) {
      this.firstInstallmentPayments[0].amount = 0;
      this.firstInstallmentPayments[1].amount = 0;
      return;
    }
    const v = Math.max(0, Number(value) || 0);
    if (this.firstInstallmentPaymentMode === 'single') {
      this.firstInstallmentPayments[0].amount = Math.min(v, total);
      this.firstInstallmentPayments[1].amount = 0;
      return;
    }
    if (index === 0) {
      const first = Math.min(v, total);
      this.firstInstallmentPayments[0].amount = first;
      this.firstInstallmentPayments[1].amount = total - first;
    } else {
      const second = Math.min(v, total);
      this.firstInstallmentPayments[1].amount = second;
      this.firstInstallmentPayments[0].amount = total - second;
    }
  }

  onDepositMethodChange(index: number, method: string): void {
    if (!this.depositPayments[index]) return;
    this.depositPayments[index].method = method;
    if (method === 'CREDIT_CARD') {
      this.depositPaymentMode = 'split';
      this.recalculateDepositSplit();
    }
  }

  hasDepositBoleto(): boolean {
    return this.depositPayments?.some(p => p.method === 'BOLETO') ?? false;
  }

  hasDepositCard(): boolean {
    return this.depositPayments?.some(p => p.method === 'CREDIT_CARD') ?? false;
  }

  private validatePaymentSplits(): boolean {
    const depositTotal = this.getDepositTotal();
    const depositSplitSum = this.depositPayments
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    if (depositTotal >= 0 && depositSplitSum > depositTotal + 0.01) {
      this.errorService.showErrorMessage('A soma dos valores da caução não pode ultrapassar o valor total da diferença de caução.');
      return false;
    }

    const firstInstallmentTotal = this.getFirstInstallmentTotal();
    const firstInstallmentSplitSum = this.firstInstallmentPayments
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    if (firstInstallmentTotal >= 0 && firstInstallmentSplitSum > firstInstallmentTotal + 0.01) {
      this.errorService.showErrorMessage('A soma dos valores da 1ª parcela não pode ultrapassar o valor total da 1ª parcela.');
      return false;
    }

    return true;
  }

  /**
   * Grava nas observações do placeholder (`observacoes`) se a operação foi renovação ou migração.
   * Preserva JSON existente e concatena sem sobrescrever texto já salvo em chaves de observação.
   */
  private mergeRenewalObservationIntoPlaceholders(
    placeholdersJson: string | undefined | null,
    operationType: 'renewal' | 'migration'
  ): string {
    let obj: Record<string, unknown> = {};
    const raw = (placeholdersJson ?? '').trim();
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          obj = { ...(parsed as Record<string, unknown>) };
        }
      } catch {
        obj = { observacoesLegadoNaoJson: raw.slice(0, 500) };
      }
    }

    const when = new Date().toLocaleString('pt-BR');
    const line =
      operationType === 'migration'
        ? `Contrato migrado — registrado pelo usuário em ${when}.`
        : `Contrato renovado — registrado pelo usuário em ${when}.`;

    let existing = '';
    for (const k of Object.keys(obj)) {
      const fold = k
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
      if (fold === 'observacoes' && typeof obj[k] === 'string') {
        existing = String(obj[k]).trim();
        break;
      }
    }

    const mergedObs = existing ? `${existing}\n\n${line}` : line;
    obj['observacoes'] = mergedObs;
    obj['_erpRenewOrMigrate'] = operationType === 'migration' ? 'migracao' : 'renovacao';
    obj['_erpRenewOrMigrateAtLocal'] = when;

    return JSON.stringify(obj);
  }

  // ======== Steps ========

  goToStep(step: number): void {
    this.currentStep = step;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ======== Submit ========

  onSubmit(): void {
    if (this.contractForm.invalid) {
      this.errorService.showErrorMessage('Preencha todos os campos obrigatórios');
      return;
    }
    if (!this.validatePaymentSplits()) {
      return;
    }
    this.renewContract();
  }

  get isProductChanged(): boolean {
    const newProductId = Number(this.contractForm.get('productId')?.value);
    return !!this.previousProductId && !!newProductId && newProductId !== this.previousProductId;
  }

  renewContract(): void {
    this.isSaving = true;
    const formValue = this.contractForm.getRawValue();
    const today = this.getTodayDateString();
    const opType =
      formValue.renewalOperationType === 'migration' ? 'migration' : 'renewal';
    const placeholdersWithObservation = this.mergeRenewalObservationIntoPlaceholders(
      formValue.placeholdersDataJson,
      opType
    );
    const renewalData = {
      /** Backend usa para distinguir renovação simples vs migração de plano. */
      migracaoDePlano: opType === 'migration',
      contract: {
        customerId: formValue.customerId || 1,
        vehicleId: formValue.vehicleId || 1,
        productId: formValue.productId,
        documentTemplateVersionId: formValue.documentTemplateVersionId || 2,
        startDate: this.formatDateForAPI(today),
        endDate: this.formatDateForAPI(formValue.endDate),
        billingCycle: formValue.billingCycle,
        status: 'Pend. Assin.',
        placeholdersDataJson: placeholdersWithObservation,
        signatureProvider: formValue.signatureProvider || 'ClickSign'
      },
      driver: {
        name: formValue.driverName,
        cpfCnpj: formValue.driverCpfCnpj,
        cnhNumber: formValue.driverCnhNumber,
        cnhExpiryDate: formValue.driverCnhExpiryDate ? this.formatDateForAPI(formValue.driverCnhExpiryDate) : null,
        email: formValue.driverEmail,
        phone: formValue.driverPhone,
        street: formValue.driverStreet,
        number: formValue.driverNumber,
        complement: formValue.driverComplement || '',
        district: formValue.driverDistrict,
        city: formValue.driverCity,
        state: formValue.driverState,
        zipCode: formValue.driverZipCode
      },
      vehicleSnapshot: {
        plate: formValue.vehiclePlate,
        model: formValue.vehicleModel,
        year: formValue.vehicleYear,
        color: formValue.vehicleColor || '',
        brand: formValue.vehicleBrand || '',
        renavam: formValue.vehicleRenavam || '',
        chassis: formValue.vehicleChassis || '',
        currentKm: formValue.vehicleCurrentKm || 0,
        inspectionNotes: formValue.vehicleInspectionNotes || ''
      }
    };

    // Se o plano mudou, cancelamos os boletos pendentes do contrato anterior,
    // MAS mantendo a caução e a 1ª parcela caso elas ainda estejam pendentes.
    const cancelPending$: Observable<any> = (this.isProductChanged && this.asaasCustomerId)
      ? this.paymentService.listAsaasCharges({
          customer: this.asaasCustomerId,
          status: 'PENDING',
          limit: 50
        }).pipe(
          switchMap((chargesRes) => {
            const charges = chargesRes?.data || chargesRes?.Data || [];
            const oldDepositRef = `CONTRATO-${this.contractId}-CAUCAO`;
            const oldFirstRef = `CONTRATO-${this.contractId}-P1-`;

            const keepDeposit = !this.previousDepositPaid;
            const keepFirst = !this.previousFirstInstallmentPaid;

            const toCancel = (charges as any[]).filter((c) => {
              const ref = (c?.externalReference || '').toString();
              const isOldDeposit = ref.includes(oldDepositRef);
              const isOldFirst = ref.includes(oldFirstRef);
              const keep =
                (isOldDeposit && keepDeposit) ||
                (isOldFirst && keepFirst);
              return !keep;
            });

            if (!toCancel.length) {
              return of(undefined);
            }

            return forkJoin(
              toCancel.map((c) =>
                this.paymentService.cancelAsaasCharge(c.id).pipe(
                  catchError(() => of(null))
                )
              )
            ).pipe(map(() => undefined));
          }),
          catchError((err) => {
            this.errorService.showErrorMessage(
              'Aviso: erro ao cancelar boletos pendentes do contrato anterior. ' +
              (err?.message || 'Erro desconhecido')
            );
            return of(undefined);
          })
        )
      : of(undefined);

    cancelPending$.pipe(
      switchMap(() => this.contractsService.renewContract(this.contractId, renewalData)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        const contractId = response?.contract?.id || this.contractId;

        if (this.isProductChanged && this.asaasCustomerId) {
          this.errorService.showInfoMessage(
            'Boletos pendentes do contrato anterior foram cancelados (exceto caução e 1ª parcela ainda pendentes). Novos boletos gerados a partir da data de renovação.'
          );
        }

        const payments$ = this.asaasCustomerId
          ? this.triggerAsaasPayments(response?.contract || { id: contractId, customerId: formValue.customerId }, formValue)
          : of(undefined);

        payments$.subscribe({
          next: () => {
            this.isSaving = false;
            this.errorService.showInfoMessage(
              response.message ||
                (opType === 'migration'
                  ? 'Contrato migrado com sucesso!'
                  : 'Contrato renovado com sucesso!')
            );
            this.router.navigate(['/contracts', contractId, 'view']);
          },
          error: () => {
            this.isSaving = false;
            this.router.navigate(['/contracts', contractId, 'view']);
          }
        });
      },
      error: (err) => {
        this.errorService.showErrorMessage('Erro ao renovar ou migrar contrato: ' + err.message);
        this.isSaving = false;
      }
    });
  }

  // ======== Asaas Payments + Contas a Receber ========

  private triggerAsaasPayments(createdContract: any, formValue: any): Observable<void> {
    const customerId = this.asaasCustomerId;
    if (!customerId) {
      return of(undefined);
    }

    const today = this.getTodayDateString();
    const dueDateBase = formValue.startDate || today;
    const observables: Observable<unknown>[] = [];
    const arBatch: AccountsReceivableCreate[] = [];

    const customerIdForAr =
      Number(createdContract?.customerId ?? formValue.customerId ?? 0) || undefined;
    const contractNumberForAr =
      createdContract?.contractNumber?.toString?.() ?? createdContract?.id?.toString?.() ?? '';

    const onError = (msg: string) => (err: any) => {
      this.errorService.showErrorMessage(msg + (err?.error?.message || err?.message || 'Erro desconhecido'));
      return of(null);
    };

    const addAccountsReceivable = (options: {
      value: number;
      dueDate: string;
      description: string;
      externalReference?: string;
      status?: 'Pendente' | 'Recebido' | 'Vencido' | 'Cancelado' | string;
      amountReceived?: number;
      installmentNumber?: number;
      totalInstallments?: number;
    }) => {
      if (!customerIdForAr) return;
      const docNumber = options.externalReference || `CTR-${createdContract.id}-${options.installmentNumber || 1}`;
      arBatch.push({
        contractNumber: contractNumberForAr || undefined,
        documentNumber: docNumber,
        documentType: 'CTR',
        issueDate: today,
        dueDate: options.dueDate,
        originalAmount: Number(options.value),
        amountToReceive: Number(options.value),
        status: options.status || 'Pendente',
        category: 'Venda',
        customerId: customerIdForAr,
        costCenterId: 1,
        description: options.description,
        invoiceNumber: undefined,
        saleOrderNumber: `RENOVACAO-${createdContract.id}`,
        installmentNumber: options.installmentNumber,
        totalInstallments: options.totalInstallments,
        amountReceived:
          options.status === 'Recebido'
            ? (options.amountReceived ?? Number(options.value))
            : undefined
      });
    };

    // 1) 1ª parcela: PIX/BOLETO via Asaas; CASH e CARTÃO apenas registram no Contas a Receber como pago
    this.firstInstallmentPayments
      .filter(p => p.amount && p.amount > 0)
      .forEach((p, index) => {
        const method = p.method;
        const value = Number(p.amount);
        const externalReference = `RENOVACAO-${createdContract.id}-P1-${method}-${index + 1}`;

        if (method === 'PIX' || method === 'BOLETO') {
          const charge: AsaasCharge = {
            customerId,
            billingType: method as 'PIX' | 'BOLETO',
            value,
            dueDate: dueDateBase,
            description: `Renovação - 1ª Parcela - ${method}`,
            externalReference
          };

          addAccountsReceivable({
            value: charge.value,
            dueDate: charge.dueDate,
            description: charge.description || 'Renovação - 1ª Parcela',
            externalReference: charge.externalReference,
            installmentNumber: 1,
            totalInstallments: this.contractWeeksCount || 1,
            status: 'Pendente'
          });

          observables.push(
            this.paymentService.createAsaasCharge(charge).pipe(catchError(onError('Erro ao gerar cobrança 1ª parcela: ')))
          );
          return;
        }

        if (method === 'CASH' || method === 'CREDIT_CARD') {
          addAccountsReceivable({
            value,
            dueDate: dueDateBase,
            description: `Renovação - 1ª Parcela - ${method}`,
            externalReference,
            installmentNumber: 1,
            totalInstallments: this.contractWeeksCount || 1,
            status: 'Recebido',
            amountReceived: value
          });
          return;
        }
      });

    // 2) Caução (diferença) em PIX
    if (this.depositDifference > 0) {
      this.depositPayments
        .filter(p => p.amount && p.amount > 0 && p.method === 'PIX')
        .forEach((p, index) => {
          const charge: AsaasCharge = {
            customerId,
            billingType: 'PIX',
            value: Number(p.amount),
            dueDate: dueDateBase,
            description: `Renovação - Diferença Caução - PIX`,
            externalReference: `RENOVACAO-${createdContract.id}-CAUCAO-PIX-${index + 1}`
          };
          addAccountsReceivable({
            value: charge.value,
            dueDate: charge.dueDate,
            description: charge.description || 'Renovação - Diferença Caução - PIX',
            externalReference: charge.externalReference
          });
          observables.push(
            this.paymentService.createAsaasCharge(charge).pipe(catchError(onError('Erro ao gerar cobrança PIX da caução: ')))
          );
        });
    }

    // 2.1) Caução (diferença) em DINHEIRO (CASH) - não existe no Asaas
    // Registra apenas no Contas a Receber como "Recebido".
    if (this.depositDifference > 0) {
      this.depositPayments
        .filter(p => p.amount && p.amount > 0 && p.method === 'CASH')
        .forEach((p, index) => {
          const value = Number(p.amount);
          const externalReference = `RENOVACAO-${createdContract.id}-CAUCAO-CASH-${index + 1}`;
          addAccountsReceivable({
            value,
            dueDate: dueDateBase,
            description: 'Renovação - Diferença Caução - Dinheiro',
            externalReference,
            status: 'Recebido',
            amountReceived: value
          });
        });
    }

    // 3) Caução (diferença) em CARTÃO
    if (this.depositDifference > 0) {
      const cardDeposits = this.depositPayments
        .filter(p => p.amount && p.amount > 0 && p.method === 'CREDIT_CARD');

      if (cardDeposits.length > 0) {
        if (!this.asaasCustomerInfo) {
          this.errorService.showErrorMessage('Não foi possível carregar os dados do cliente Asaas para pagamento com cartão.');
        } else if (!this.creditCardNumber || !this.creditCardExpiryMonth || !this.creditCardExpiryYear || !this.creditCardCcv) {
          this.errorService.showErrorMessage('Preencha todos os dados do cartão para realizar o pagamento da caução.');
        } else {
          const customerInfo = this.asaasCustomerInfo as AsaasCustomer;

          cardDeposits.forEach((p, index) => {
            const value = Number(p.amount);
            if (!value || value <= 0) return;

            const useSecondCard = cardDeposits.length > 1 && index === 1;
            const holderName = useSecondCard
              ? (this.creditCardHolderName2 || customerInfo.name)
              : (this.creditCardHolderName || customerInfo.name);
            const cardNumber = useSecondCard ? this.creditCardNumber2 : this.creditCardNumber;
            const expiryMonth = useSecondCard ? this.creditCardExpiryMonth2 : this.creditCardExpiryMonth;
            const expiryYear = useSecondCard ? this.creditCardExpiryYear2 : this.creditCardExpiryYear;
            const cvv = useSecondCard ? this.creditCardCcv2 : this.creditCardCcv;
            const installmentsForThisCard = Math.max(
              1,
              Math.min(10, useSecondCard ? (this.depositCardInstallments2 || 1) : (this.depositCardInstallments || 1))
            );

            const charge: AsaasCharge = {
              customerId,
              billingType: 'CREDIT_CARD',
              value,
              dueDate: dueDateBase,
              description: `Renovação - Diferença Caução - Cartão (${installmentsForThisCard}x) - parte ${index + 1}/${cardDeposits.length}`,
              externalReference: `RENOVACAO-${createdContract.id}-CAUCAO-CARTAO-${index + 1}`,
              installmentCount: installmentsForThisCard,
              creditCard: {
                holderName,
                number: cardNumber,
                expiryMonth,
                expiryYear,
                ccv: cvv
              },
              creditCardHolderInfo: {
                name: customerInfo.name,
                email: customerInfo.email || formValue.driverEmail,
                cpfCnpj: customerInfo.cpfCnpj,
                postalCode: customerInfo.postalCode || formValue.driverZipCode,
                addressNumber: customerInfo.addressNumber || formValue.driverNumber,
                addressComplement: customerInfo.addressComplement || formValue.driverComplement,
                phone: customerInfo.phone || formValue.driverPhone,
                mobilePhone: customerInfo.mobilePhone || formValue.driverPhone
              }
            };

            addAccountsReceivable({
              value: charge.value,
              dueDate: charge.dueDate,
              description: charge.description || 'Renovação - Diferença Caução - Cartão',
              externalReference: charge.externalReference,
              status: 'Recebido',
              amountReceived: charge.value
            });

            observables.push(
              this.paymentService.createAsaasCharge(charge).pipe(
                catchError(onError('Erro ao gerar cobrança no cartão da caução: '))
              )
            );
          });
        }
      }
    }

    // 4) Caução (diferença) em BOLETO
    if (this.depositDifference > 0) {
      const boletoTotal = this.depositPayments
        .filter(p => p.amount && p.amount > 0 && p.method === 'BOLETO')
        .reduce((sum, p) => sum + Number(p.amount), 0);
      const installments = Math.max(1, Math.min(10, this.depositInstallments || 1));

      if (boletoTotal > 0 && installments === 1) {
        const charge: AsaasCharge = {
          customerId,
          billingType: 'BOLETO',
          value: boletoTotal,
          dueDate: dueDateBase,
          description: 'Renovação - Diferença Caução - Boleto à vista',
          externalReference: `RENOVACAO-${createdContract.id}-CAUCAO-1`
        };
        addAccountsReceivable({
          value: charge.value,
          dueDate: charge.dueDate,
          description: charge.description || 'Renovação - Diferença Caução - Boleto à vista',
          externalReference: charge.externalReference
        });
        observables.push(
          this.paymentService.createAsaasCharge(charge).pipe(catchError(onError('Erro ao gerar boleto da caução: ')))
        );
      } else if (boletoTotal > 0 && installments > 1) {
        const totalCents = Math.round(boletoTotal * 100);
        const baseCents = Math.floor(totalCents / installments);
        const depositBatch: AsaasChargeBatch[] = [];
        for (let i = 0; i < installments; i++) {
          const isLast = i === installments - 1;
          const cents = isLast ? totalCents - baseCents * i : baseCents;
          depositBatch.push({
            customerId,
            value: cents / 100,
            dueDate: dueDateBase,
            description: `Renovação - Diferença Caução - parcela ${i + 1}/${installments}`,
            externalReference: `RENOVACAO-${createdContract.id}-CAUCAO-${i + 1}`
          });
        }
        depositBatch.forEach((item, index) => {
          addAccountsReceivable({
            value: item.value,
            dueDate: item.dueDate,
            description: item.description || `Renovação - Diferença Caução - parcela ${index + 1}/${installments}`,
            externalReference: item.externalReference,
            installmentNumber: index + 1,
            totalInstallments: installments
          });
        });
        observables.push(
          this.paymentService.createAsaasChargeBatch(depositBatch).pipe(catchError(onError('Erro ao gerar boletos da caução: ')))
        );
      }
    }

    // 5) Plano (aluguel semanal)
    const totalInstallments = this.contractWeeksCount || 0;
    if (totalInstallments > 0 && this.newBaseAmount && customerId) {
      const planBatch: AsaasChargeBatch[] = [];
      const parcelaValue = this.newBaseAmount;
      const startDate = formValue.startDate || today;
      for (let i = 0; i < totalInstallments; i++) {
        const dueDate = this.addDaysToDateString(startDate, i * 7);
        planBatch.push({
          customerId,
          value: parcelaValue,
          dueDate,
          description: `Renovação - Aluguel - parcela ${i + 1}/${totalInstallments} (${formValue.billingCycle || 'Semanal'})`,
          externalReference: `RENOVACAO-${createdContract.id}-PLANO-${i + 1}`
        });
      }
      planBatch.forEach((item, index) => {
        addAccountsReceivable({
          value: item.value,
          dueDate: item.dueDate,
          description: item.description || `Renovação - Aluguel - parcela ${index + 1}/${totalInstallments}`,
          externalReference: item.externalReference,
          installmentNumber: index + 1,
          totalInstallments
        });
      });
      observables.push(
        this.paymentService.createAsaasChargeBatch(planBatch).pipe(catchError(onError('Erro ao gerar boletos do plano: ')))
      );
    }

    if (arBatch.length > 0 && customerIdForAr) {
      observables.push(
        this.accountsReceivableService.createAccountsReceivableBatch(arBatch)
          .pipe(catchError(onError('Erro ao criar contas a receber: ')))
      );
    }

    if (observables.length === 0) {
      return of(undefined);
    }
    return forkJoin(observables).pipe(
      catchError(() => of(null)),
      map(() => undefined),
      takeUntil(this.destroy$)
    );
  }

  // ======== Cartão UI helpers ========

  get cardBrand(): string {
    const digits = (this.creditCardNumber || '').replace(/\D/g, '');
    if (!digits) return 'Cartão';
    if (/^4[0-9]{0,}$/.test(digits)) return 'Visa';
    if (/^5[1-5][0-9]{0,}$/.test(digits)) return 'Mastercard';
    if (/^3[47][0-9]{0,}$/.test(digits)) return 'American Express';
    if (/^6(?:011|5)/.test(digits)) return 'Discover';
    return 'Cartão';
  }

  get maskedCardNumber(): string {
    const digits = (this.creditCardNumber || '').replace(/\D/g, '').slice(0, 16).padEnd(16, '•');
    return [digits.slice(0, 4), digits.slice(4, 8), digits.slice(8, 12), digits.slice(12, 16)].join(' ').trim();
  }

  onCreditCardNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let digits = (input.value || '').replace(/\D/g, '').slice(0, 16);
    const groups = digits.match(/.{1,4}/g) || [];
    this.creditCardNumber = groups.join(' ');
  }

  get cardBrand2(): string {
    const digits = (this.creditCardNumber2 || '').replace(/\D/g, '');
    if (!digits) return 'Cartão';
    if (/^4[0-9]{0,}$/.test(digits)) return 'Visa';
    if (/^5[1-5][0-9]{0,}$/.test(digits)) return 'Mastercard';
    if (/^3[47][0-9]{0,}$/.test(digits)) return 'American Express';
    if (/^6(?:011|5)/.test(digits)) return 'Discover';
    return 'Cartão';
  }

  get maskedCardNumber2(): string {
    const digits = (this.creditCardNumber2 || '').replace(/\D/g, '').slice(0, 16).padEnd(16, '•');
    return [digits.slice(0, 4), digits.slice(4, 8), digits.slice(8, 12), digits.slice(12, 16)].join(' ').trim();
  }

  onCreditCardNumber2Input(event: Event): void {
    const input = event.target as HTMLInputElement;
    let digits = (input.value || '').replace(/\D/g, '').slice(0, 16);
    const groups = digits.match(/.{1,4}/g) || [];
    this.creditCardNumber2 = groups.join(' ');
  }

  setCardBack(isBack: boolean, cardIndex: 1 | 2 = 1): void {
    if (cardIndex === 1) {
      this.isCardBack = isBack;
    } else {
      this.isCardBack2 = isBack;
    }
  }

  // ======== Utilitários ========

  onCancel(): void {
    this.router.navigate(['/contracts', this.contractId, 'view']);
  }

  formatDateForInput(date: string | Date | null): string {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDateForAPI(date: string): string {
    if (!date) return '';
    return date + 'T00:00:00Z';
  }

  getTodayDateString(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private addDaysToDateString(dateStr: string, daysToAdd: number): string {
    const base = new Date(dateStr);
    if (isNaN(base.getTime())) return dateStr;
    const d = new Date(base.getTime());
    d.setDate(d.getDate() + daysToAdd);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  formatPrice(price: number): string {
    if (!price && price !== 0) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  }

  get submitButtonText(): string {
    if (this.isSaving) {
      return 'Processando...';
    }
    const op = this.contractForm?.get('renewalOperationType')?.value;
    return op === 'migration' ? 'Confirmar migração' : 'Confirmar renovação';
  }

  get isSubmitDisabled(): boolean {
    return this.contractForm.invalid || this.isLoading || this.isSaving;
  }
}
